import React, { useLayoutEffect, useState } from 'react';
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiArrowUpSLine,
  RiCheckboxMultipleBlankLine,
  RiInformationLine,
  RiLinkM,
} from 'react-icons/ri';

import {
  Button,
  Divider,
  EnableToggle,
  HelperText,
  Label,
  TextInput,
} from '../../../../../components/UiComponents/Ui';
import { redirectsSchema } from './redirectsValidator';
import {
  getSupplierRedirectDetails,
  updateSupplierRedirects,
} from './redirectsActions';
import RedirectTabSkeleton from './RedirectTabSkeleton';
import { useGlobalContext } from '../../../../../Contexts/GlobalContext';
import { useQuery } from '@tanstack/react-query';
import { getError, handleChange } from '../../../../helperFunctions';
import ErrorEmptyStateView from '../../../../../components/ErrorEmptyStateView';
import SubmitButton from '../../../../../components/UiComponents/SubmitButton';
import MultiSelectDropdown from '../../../../../components/MultiSelectDropdown';

const redirectsLinks = [
  { label: 'Complete', value: 'border-l-success-600', name: 'complete' },
  { label: 'Terminate', value: 'border-l-warning-500', name: 'terminate' },
  {
    label: 'Security Terminate',
    value: 'border-l-error-600',
    name: 'securityTerminate',
  },
  { label: 'Quota full', value: 'border-l-primary-600', name: 'quotaFull' },
];

const encryptionTypeOptions = [
  { label: 'Base64', value: '1' },
  { label: 'UTF-8', value: '2' },
  { label: 'Hex', value: '3' },
];

const preHashData = [
  { label: 'Complete', value: 'complete' },
  { label: 'Terminate', value: 'terminate' },
  { label: 'Security Terminate', value: 'securityTerminate' },
  { label: 'Quota Full', value: 'quotaFull' },
];

const keyAndType = [
  { placeholder: 'Secret/Encryption Key', type: 'text', name: 'encryptionKey' },
  {
    placeholder: '',
    type: 'select',
    name: 'encryptionType',
    options: encryptionTypeOptions,
  },
];

const encryptionOptions = {
  1: { label: 'HMAC-SHA1', fields: keyAndType },
  2: { label: 'HMAC-SHA256', fields: keyAndType },
  3: {
    label: 'MD5',
    fields: [
      { placeholder: 'Salt/Extra Value', type: 'text', name: 'saltValue' },
    ],
  },
};

const encryptionSettings = [
  { label: 'HMAC-SHA1', value: '1' },
  { label: 'HMAC-SHA256', value: '2' },
  { label: 'MD5', value: '3' },
];

/* ------------------------------ local helpers ------------------------------ */
const ToggleRow = ({ title, description, ...toggle }) => (
  <div className="flex items-center justify-between">
    <div className="space-y-1">
      <h2 className="text-md font-bold">{title}</h2>
      <p className="text-xs font-regular text-gray-500">{description}</p>
    </div>
    <EnableToggle {...toggle} />
  </div>
);

const SettingsCard = ({ title, description, children }) => (
  <div className="space-y-[14px] rounded-xl border border-gray-200 bg-gray-25 px-6 py-3 shadow-cardShadow">
    <div className="space-y-1">
      <h2 className="text-md font-bold text-gray-800">{title}</h2>
      <p className="text-text-xs text-gray-500">{description}</p>
    </div>
    {children}
  </div>
);

const RedirectsTab = ({ supplierID }) => {
  const { setSnackbar } = useGlobalContext();
  const [redirectsData, setRedirectsData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  const {
    data: redirectDetails,
    refetch: redirectDetailsMutate,
    isLoading: redirectsDetailsLoading,
    error: redirectsDetailsError,
  } = useQuery({
    queryKey: ['supplier-redirect-details', supplierID],
    queryFn: () => getSupplierRedirectDetails({ supplierID }),
  });

  const handleClose = () => {
    setFormErrors({});
    setRedirectsData({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const validationResult = redirectsSchema.safeParse({
        ...redirectsData,
        _id: supplierID,
      });
      if (validationResult.success) {
        const response = await updateSupplierRedirects({
          ...validationResult.data,
          _id: supplierID,
        });
        if (!response?.error) {
          setSnackbar({
            open: true,
            msg: 'Supplier Redirects updated successfully!',
            severity: 'success',
          });
          redirectDetailsMutate();
          handleClose();
        } else {
          setSnackbar({
            open: true,
            msg: 'Internal Server Error! Please try again!',
            severity: 'error',
          });
        }
      } else {
        setFormErrors(formatZodError(validationResult.error.errors));
      }
    } catch (error) {
      console.log('error in handleSubmit from edit Supplier Redirects', error);
      setSnackbar({
        open: true,
        msg: 'Internal Server Error! Please try again!',
        severity: 'error',
      });
      handleClose();
    }
  };

  useLayoutEffect(() => {
    if (redirectDetails) {
      setRedirectsData({
        ...redirectDetails?.details,
        ...redirectDetails?.customRedirects,
      });
    }
  }, [redirectDetails]);

  // auto-generate the preview link from the global "Complete" redirect + hash settings
  useLayoutEffect(() => {
    if (redirectsData?.globalRedirects && redirectsData?.redirects?.complete) {
      const link = `${redirectsData.redirects.complete}${
        redirectsData?.hashName
          ? `&${redirectsData.hashName}=${redirectsData?.preHash ? '[%preHashValue%]' : ''}[%hashValue%]`
          : ''
      }`;
      setRedirectsData((prev) => ({ ...prev, previewLink: link }));
    }
  }, [
    redirectsData?.hashName,
    redirectsData?.preHash,
    redirectsData?.redirects?.complete,
    redirectsData?.globalRedirects,
  ]);

  const copyRedirect = (url, label) => {
    if (typeof window === 'undefined' || !url) return;
    navigator.clipboard.writeText(url);
    setSnackbar({
      open: true,
      message: `${label} Redirect Copied!`,
      type: 'success',
    });
  };

  const onChange = (path, value) =>
    handleChange(path, value, setRedirectsData, setFormErrors, formErrors);

  if (redirectsDetailsLoading) return <RedirectTabSkeleton />;

  if (redirectsDetailsError) {
    return (
      <ErrorEmptyStateView
        title="Something went wrong!"
        description="Something went wrong!"
        type="error"
      />
    );
  }

  const algorithmError = getError(['encryptionAlgorithm'], formErrors);
  const hashNameError = getError(['hashName'], formErrors);
  const previewError = getError(['previewLink'], formErrors);

  return (
    <div className=" space-y-5 ">
      <form onSubmit={handleSubmit} className="space-y-[18px]">
        <div className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-black">Redirect Settings</h2>
            <p className="text-sm font-[400] text-gray-600">
              Configure your settings from here.
            </p>
          </div>

          <Divider />

          <div className="space-y-[18px]">
            <ToggleRow
              id="global-redirects"
              title={
                <>
                  Enable <span className="text-blue-600">Global Redirects</span>
                </>
              }
              description="Redirect all respondents using the default redirect settings."
              checked={redirectsData?.globalRedirects}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setRedirectsData((prev) => ({
                  ...prev,
                  globalRedirects: isChecked,
                  countryRedirects: !isChecked,
                  ...(isChecked && {
                    encryptionAlgorithm: { label: 'HMAC-SHA1', value: '1' },
                    encryptionType: { label: 'Base64', value: '1' },
                  }),
                }));
              }}
            />
            <ToggleRow
              id="country-redirects"
              title={
                <>
                  Enable{' '}
                  <span className="text-purple-600">Country Redirects</span>
                </>
              }
              description="Redirect all respondents using the default redirect settings."
              checked={redirectsData?.countryRedirects}
              onChange={(e) =>
                setRedirectsData((prev) => ({
                  ...prev,
                  countryRedirects: e.target.checked,
                  globalRedirects: !e.target.checked,
                }))
              }
            />
          </div>

          {/* global redirect urls */}
          {redirectsData?.globalRedirects && (
            <div className="space-y-3 rounded-lg bg-gray-25 px-[18px] py-3 shadow-cardShadow">
              <h2 className="text-sm font-semibold text-black">
                Global Redirects
              </h2>
              <Divider />
              <div className="space-y-3">
                {redirectsLinks.map((d) => {
                  const url = redirectsData?.redirects?.[d.name] || '';
                  const err = getError(['redirects', d.name], formErrors);
                  return (
                    <div
                      className="flex items-start justify-between"
                      key={d.name}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-[40px] border-l-[1.5px] ${d.value}`}
                        />
                        <h2 className="text-sm font-medium text-gray-800">
                          {d.label}
                        </h2>
                      </div>
                      <div className="w-[60%] space-y-1">
                        <TextInput
                          type="url"
                          placeholder="https://xyz.com/xyz?qid=[%rid%]&status=1"
                          required={redirectsData?.globalRedirects}
                          endIcon={
                            <button
                              type="button"
                              aria-label={`Copy ${d.label} redirect`}
                              className="text-gray-500 hover:text-gray-700"
                              onClick={() => copyRedirect(url, d.label)}
                            >
                              <RiCheckboxMultipleBlankLine size={18} />
                            </button>
                          }
                          value={url}
                          error={err}
                          onChange={(e) =>
                            onChange(['redirects', d.name], e.target.value)
                          }
                        />
                        {!!err && <HelperText>{err}</HelperText>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Divider />

          {redirectsData?.globalRedirects && (
            <div className="space-y-4">
              <ToggleRow
                id="custom-redirects"
                title="Custom Redirection"
                description="Enable or Disable custom redirection"
                checked={redirectsData?.customRedirection}
                onChange={(e) =>
                  onChange(['customRedirection'], e.target.checked)
                }
              />

              {redirectsData?.customRedirection && (
                <>
                  {/* encryption */}
                  <SettingsCard
                    title="Encryption Settings"
                    description="Configure the encryption algorithm and related settings"
                  >
                    <div className="space-y-[14px]">
                      <div className="space-y-1">
                        <div
                          className={`rounded-md ${algorithmError ? 'border border-red-500' : ''}`}
                        >
                          <MultiSelectDropdown
                            options={encryptionSettings}
                            dropdownText="Encryption Settings..."
                            selected={[
                              {
                                label:
                                  redirectsData?.encryptionAlgorithm?.label ||
                                  '',
                                value:
                                  redirectsData?.encryptionAlgorithm?.value ||
                                  '',
                              },
                            ]}
                            setSelected={(e) => {
                              const selectedValue = e;
                              const obj = {
                                ...redirectsData,
                                encryptionAlgorithm: selectedValue?.[0],
                                encryptionType: { label: 'Base64', value: '1' },
                              };
                              if (selectedValue?.[0]?.value !== '3')
                                delete obj?.saltValue;
                              if (selectedValue?.[0]?.value === '3') {
                                delete obj?.encryptionType;
                                delete obj?.encryptionKey;
                              }
                              if (formErrors['encryptionAlgorithm']) {
                                const {
                                  ['encryptionAlgorithm']: _removed,
                                  ...newObject
                                } = formErrors;
                                setFormErrors(newObject);
                              }
                              setRedirectsData(obj);
                            }}
                          />
                        </div>
                        {!!algorithmError && (
                          <HelperText>{algorithmError}</HelperText>
                        )}
                      </div>

                      {encryptionOptions[
                        Number(redirectsData.encryptionAlgorithm?.value)
                      ]?.fields.map((field) => {
                        const err = getError([field.name], formErrors);
                        if (field.type === 'select') {
                          return (
                            <div key={field.name} className="space-y-1">
                              <div
                                className={`rounded-md ${err ? 'border border-red-500' : ''}`}
                              >
                                <MultiSelectDropdown
                                  options={
                                    field.options || [
                                      { label: 'Base64', value: '1' },
                                    ]
                                  }
                                  dropdownText="Select Encryption Type..."
                                  selected={[
                                    {
                                      label:
                                        redirectsData?.encryptionType?.label ||
                                        '',
                                      value:
                                        redirectsData?.encryptionType?.value ||
                                        '',
                                    },
                                  ]}
                                  setSelected={(e) => {
                                    const selectedValue = e;
                                    onChange(['encryptionType'], {
                                      label: selectedValue?.[0]?.label,
                                      value: selectedValue?.[0]?.value,
                                    });
                                  }}
                                />
                              </div>
                              {!!err && <HelperText>{err}</HelperText>}
                            </div>
                          );
                        }
                        return (
                          <div key={field.name} className="space-y-1">
                            <TextInput
                              type={field.type}
                              required
                              placeholder={field.placeholder}
                              value={redirectsData?.[field.name] || ''}
                              error={err}
                              onChange={(e) =>
                                onChange([field.name], e.target.value)
                              }
                            />
                            {!!err && <HelperText>{err}</HelperText>}
                          </div>
                        );
                      })}
                    </div>
                  </SettingsCard>

                  {/* url configuration */}
                  <SettingsCard
                    title="URL Configuration"
                    description="Configure URL parameters and templates"
                  >
                    <div className="space-y-[16px]">
                      <div className="space-y-[6px]">
                        <Label required>Hash Name</Label>
                        <div className="space-y-1">
                          <TextInput
                            required
                            value={redirectsData?.hashName || ''}
                            error={hashNameError}
                            onChange={(e) =>
                              onChange(['hashName'], e.target.value)
                            }
                          />
                          <div className="flex items-center gap-[3px]">
                            <RiInformationLine
                              size={16}
                              className="text-gray-500"
                            />
                            <HelperText error={false}>
                              The hash name to be used in the URL
                            </HelperText>
                          </div>
                          {!!hashNameError && (
                            <HelperText>{hashNameError}</HelperText>
                          )}
                        </div>
                      </div>
                      <div className="space-y-[6px]">
                        <Label>Preview Link</Label>
                        <div className="space-y-1">
                          <TextInput
                            type="url"
                            disabled
                            startIcon={<RiLinkM size={16} />}
                            placeholder="Preview link"
                            value={redirectsData?.previewLink || ''}
                            error={previewError}
                          />
                          {!!previewError && (
                            <HelperText>{previewError}</HelperText>
                          )}
                        </div>
                      </div>
                    </div>
                  </SettingsCard>

                  {/* advanced options */}
                  <div className="space-y-[14px] rounded-xl border border-gray-200 bg-gray-25 px-6 py-4 shadow-cardShadow">
                    <button
                      type="button"
                      aria-expanded={!!redirectsData?.showAdvancedOptions}
                      className="flex w-full cursor-pointer items-center justify-between"
                      onClick={() =>
                        onChange(
                          ['showAdvancedOptions'],
                          !redirectsData.showAdvancedOptions,
                        )
                      }
                    >
                      <h2 className="text-md font-[700] text-gray-800">
                        Advanced Options
                      </h2>
                      {redirectsData?.showAdvancedOptions ? (
                        <RiArrowUpSLine size={20} />
                      ) : (
                        <RiArrowDownSLine size={20} />
                      )}
                    </button>

                    {redirectsData?.showAdvancedOptions && (
                      <div className="space-y-[12px]">
                        <ToggleRow
                          id="show-advance-options"
                          title="Pre-Hash"
                          description="Enable or Disable Pre-Hash"
                          checked={redirectsData?.preHash}
                          onChange={(e) =>
                            onChange(['preHash'], e.target.checked)
                          }
                        />
                        {redirectsData?.preHash && (
                          <div className="grid grid-cols-4 gap-[10px]">
                            {preHashData.map((data) => {
                              const err =
                                getError(
                                  ['preHashValues', data.value],
                                  formErrors,
                                ) || formErrors?.[data.value];
                              return (
                                <div key={data.value} className="space-y-[6px]">
                                  <Label required>{data.label}</Label>
                                  <div className="space-y-1">
                                    <TextInput
                                      placeholder="Pre-Hash Value"
                                      required={redirectsData?.preHash}
                                      value={
                                        redirectsData?.preHashValues?.[
                                          data.value
                                        ] || ''
                                      }
                                      error={err}
                                      onChange={(e) =>
                                        onChange(
                                          ['preHashValues', data.value],
                                          e.target.value,
                                        )
                                      }
                                    />
                                    {!!err && <HelperText>{err}</HelperText>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <section className="sticky bottom-0 flex h-[10%] w-full items-center justify-end gap-3 border-t border-gray-200 bg-white py-3">
          <div className="flex justify-end gap-2">
            <Button
              variant="outlined"
              intent="default"
              type="button"
              className="w-[100px]"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <SubmitButton
              name="Save Settings"
              endIcon={<RiArrowRightSLine size={20} />}
            />
          </div>
        </section>
      </form>
    </div>
  );
};

export default RedirectsTab;
